'use client';

import { useEffect, useRef, useCallback } from 'react';

const TOTAL_FRAMES = 174;
const IMG_DIR = '/images/image-sequence/';
const IMG_PREFIX = 'ezgif-frame-';
const IMG_EXT = '.jpg';

function frameSrc(i: number) {
  return IMG_DIR + IMG_PREFIX + String(i + 1).padStart(3, '0') + IMG_EXT;
}

export default function ImageSequenceHero() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imagesRef = useRef<HTMLImageElement[]>([]);
  const stateRef = useRef({ currentFrame: -1, canvasW: 0, canvasH: 0, ticking: false });
  const scrollIndRef = useRef<HTMLDivElement>(null);

  const drawFrame = useCallback((index: number, force?: boolean) => {
    const { currentFrame, canvasW, canvasH } = stateRef.current;
    if (!force && index === currentFrame) return;
    if (index < 0) index = 0;
    if (index >= TOTAL_FRAMES) index = TOTAL_FRAMES - 1;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = imagesRef.current[index];
    if (!img || !img.complete || !img.naturalWidth) return;

    ctx.clearRect(0, 0, canvasW, canvasH);

    const imgAR = img.naturalWidth / img.naturalHeight;
    const cvAR = canvasW / canvasH;
    let sx: number, sy: number, sw: number, sh: number;

    if (cvAR > imgAR) {
      sw = img.naturalWidth;
      sh = sw / cvAR;
      sx = 0;
      sy = (img.naturalHeight - sh) / 2;
    } else {
      sh = img.naturalHeight;
      sw = sh * cvAR;
      sy = 0;
      sx = (img.naturalWidth - sw) / 2;
    }

    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvasW, canvasH);
    stateRef.current.currentFrame = index;
  }, []);

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imagesRef.current[0];
    if (!canvas || !img || !img.naturalWidth) return;

    const dpr = window.devicePixelRatio || 1;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const imgAR = img.naturalWidth / img.naturalHeight;
    const viewAR = vw / vh;

    let drawW: number, drawH: number;
    if (viewAR > imgAR) {
      drawW = vw;
      drawH = vw / imgAR;
    } else {
      drawH = vh;
      drawW = vh * imgAR;
    }

    canvas.width = drawW * dpr;
    canvas.height = drawH * dpr;
    canvas.style.width = drawW + 'px';
    canvas.style.height = drawH + 'px';

    const ctx = canvas.getContext('2d');
    if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    stateRef.current.canvasW = drawW;
    stateRef.current.canvasH = drawH;

    if (stateRef.current.currentFrame >= 0) {
      drawFrame(stateRef.current.currentFrame, true);
    }
  }, [drawFrame]);

  useEffect(() => {
    const images: HTMLImageElement[] = [];
    let loadedCount = 0;

    for (let i = 0; i < TOTAL_FRAMES; i++) {
      const img = new window.Image();
      img.decoding = 'async';
      img.src = frameSrc(i);
      img.onload = img.onerror = () => {
        loadedCount++;
        if (loadedCount === TOTAL_FRAMES) {
          imagesRef.current = images;
          resizeCanvas();
          drawFrame(0, true);
        }
      };
      images[i] = img;
    }
    imagesRef.current = images;

    const onScroll = () => {
      if (stateRef.current.ticking) return;
      stateRef.current.ticking = true;

      requestAnimationFrame(() => {
        const section = sectionRef.current;
        if (!section) { stateRef.current.ticking = false; return; }

        const rect = section.getBoundingClientRect();
        // scrollRange = total scrollable distance within the section
        // (section height minus one viewport height, since the sticky fills one viewport)
        const scrollRange = rect.height - window.innerHeight;
        if (scrollRange <= 0) { stateRef.current.ticking = false; return; }

        // rect.top starts at the section's offset from viewport top
        // As we scroll down, rect.top goes negative
        const scrolled = -rect.top;
        const progress = Math.max(0, Math.min(scrolled / scrollRange, 1));
        const frame = Math.round(progress * (TOTAL_FRAMES - 1));
        drawFrame(frame);

        if (scrollIndRef.current) {
          scrollIndRef.current.style.opacity = progress > 0.02 ? '0' : '1';
        }

        stateRef.current.ticking = false;
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', resizeCanvas);
    // Initial call in case page is already scrolled
    onScroll();

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, [drawFrame, resizeCanvas]);

  return (
    <div ref={sectionRef} className="v2-seq-section">
      <div className="v2-seq-sticky">
        {/* Canvas — full-screen animation */}
        <canvas ref={canvasRef} className="v2-seq-canvas" />

        {/* Hero content overlay */}
        <div className="v2-hero-overlay">
          <div className="v2-hero-content">
            <h1 className="v2-hero-h1">
              Precision &amp;<br />Creativity.
            </h1>
            <p className="v2-hero-body">
              Unlock your brand&apos;s potential with tailored advertising strategies.
              Combining precision and creativity to deliver impactful results.
            </p>
            <a href="#strategies" className="v2-hero-btn">
              <span className="v2-hero-btn-icon">
                <svg width="18" height="18" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </span>
              <span className="v2-hero-btn-label">Learn More</span>
            </a>
          </div>
        </div>

        {/* Scroll indicator */}
        <div ref={scrollIndRef} className="v2-scroll-ind">
          <span className="v2-scroll-ind-text">Scroll</span>
          <span className="v2-scroll-ind-dot">
            <svg width="16" height="16" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <path d="M12 5v14M5 12l7 7 7-7" />
            </svg>
          </span>
        </div>
      </div>
    </div>
  );
}
